/**
 * Bits shared by the board card and the board page. Presentation + tiny pure
 * helpers only — no fetching lives in here.
 *
 * Per-member colour ALWAYS comes from `family_members.color` (served on
 * `Message.author` by worker/routes/messages.ts, because kids cannot call the
 * adult-gated /api/family). Never a constant, never a hash of the name.
 *
 * Colour is never the only signal either: every message carries the author's NAME
 * next to the dot, so the board still reads correctly in greyscale.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ApiRequestError } from '../../lib/api';
import type { MemberSummary } from '../../shared/types';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------- controls

export const btnPrimary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sage px-5 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnQuiet =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-medium text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const textArea =
  'block w-full resize-y rounded-xl border border-border bg-surface px-3 py-3 text-base text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60';

// ---------------------------------------------------------------- people

export const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** A deactivated member's messages still show their name and colour — history stays whole. */
export function Avatar({
  name,
  color,
  size = 'md',
}: {
  name: string;
  color: string | null;
  size?: 'sm' | 'md';
}) {
  const dims = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-full font-semibold text-white',
        dims
      )}
      style={{ backgroundColor: color ?? 'var(--color-ink-faint)' }}
    >
      {initials(name)}
    </span>
  );
}

export const authorName = (author: MemberSummary | null) => author?.name ?? 'Someone';
export const authorColor = (author: MemberSummary | null) => author?.color ?? null;

// ---------------------------------------------------------------- time

/** Re-render on a timer so "2 minutes ago" doesn't sit there lying all afternoon. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now" · "10 minutes ago" · "3 hours ago" · "yesterday" · "Mar 4". */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const diff = now - then;
  if (diff < 0) return 'just now'; // clock skew — never render a message from the future
  if (diff < 45 * 1000) return 'just now';
  if (diff < 90 * 1000) return 'a minute ago';
  if (diff < HOUR) return `${Math.round(diff / MINUTE)} minutes ago`;
  if (diff < 2 * HOUR) return 'an hour ago';
  if (diff < DAY) return `${Math.floor(diff / HOUR)} hours ago`;
  if (diff < 2 * DAY) return 'yesterday';
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} days ago`;

  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** The full stamp, for the tooltip and the screen reader. */
export function fullTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Local calendar day of a timestamp — the grouping key for the full thread. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

/** "Today" · "Yesterday" · "Saturday, March 4". */
export function dayHeading(iso: string, now: number = Date.now()): string {
  const key = dayKey(iso);
  const today = dayKey(new Date(now).toISOString());
  const yesterday = dayKey(new Date(now - DAY).toISOString());
  if (key === today) return 'Today';
  if (key === yesterday) return 'Yesterday';

  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

// ---------------------------------------------------------------- errors

/**
 * Say what actually happened. Never state a cause we haven't established: an
 * unreachable API is not "you're not allowed", and a 500 is not "check your wifi".
 */
export function explainError(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiRequestError) {
    if (err.body.error === 'api_unreachable')
      return 'The app loaded, but the API is not answering on this address.';
    if (err.body.detail) return err.body.detail;
    if (err.status === 403) return 'You are not allowed to do that.';
    if (err.status === 404) return 'That message is gone.';
    if (err.status >= 500) return 'The board is up but the server had a problem. Try again.';
    return fallback;
  }
  if (err instanceof TypeError) return 'Could not reach the app. Check your connection.';
  return fallback;
}

// ---------------------------------------------------------------- shells

export function Banner({ tone, children }: { tone: 'ok' | 'error'; children: ReactNode }) {
  return (
    <p
      role="status"
      className={cx(
        'flex items-start gap-2 rounded-xl border px-3 py-2 text-sm',
        tone === 'ok'
          ? 'border-sage/30 bg-sage-soft text-sage'
          : 'border-over/40 bg-clay-soft text-clay'
      )}
    >
      <span aria-hidden="true" className="font-semibold">
        {tone === 'ok' ? '✓' : '!'}
      </span>
      <span>{children}</span>
    </p>
  );
}

export function LoadingMessages({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" role="status" aria-live="polite">
      <span className="sr-only">Loading the board…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex gap-3">
          <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-surface-sunk" />
          <div className="flex-1 space-y-2 pt-1">
            <div className="h-3 w-24 animate-pulse rounded-full bg-surface-sunk" />
            <div
              className={cx(
                'h-4 animate-pulse rounded-full bg-surface-sunk',
                i % 2 === 0 ? 'w-full' : 'w-2/3'
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="space-y-3 rounded-xl border border-over/40 bg-clay-soft p-4">
      <p className="text-sm font-medium text-ink">The board didn&rsquo;t load.</p>
      <p className="text-sm text-ink-soft">{message}</p>
      <button type="button" onClick={onRetry} className={btnSecondary}>
        Try again
      </button>
    </div>
  );
}
